import { createSelector } from "reselect";
import SourceEntity from "../../components/Publications/models/SourceEntity";

import {
  getQueryType,
  getVariantResult,
  getEntityName,
  getThreadMap,
} from "./general.selectors";
import PubmedEntry from "../../components/PubmedUserEntries/PubmedEntry";
import isPortal from "../../common/helpers/isPortal";
import {
  getSelectedGeneIndexClinical,
  getSelectedUserViewClinical,
} from "./CardsUiClinicalSelectors";

import {
  GENE,
  VARIANT,
  REGION,
} from "../../components/Publications/constants/entities";

const getPublications = (state) =>
  (state?.variantResult && state?.variantResult?.publications) || [];

const getGenesItems = createSelector(
  getPublications,
  (publications) => publications && publications.genes
);

const getVariantResultSelector = (state) => state.variantResult;

function calcEntityName(publicationCountEntry, variantSpec) {
  if (publicationCountEntry.type === VARIANT) {
    return variantSpec;
  }
  if (publicationCountEntry.type === GENE) {
    return publicationCountEntry.symbol;
  }
  if (publicationCountEntry.type === REGION) {
    return publicationCountEntry.id;
  }
  return "";
}

const getPublicationCountEntities = createSelector(
  getVariantResultSelector,
  getEntityName,
  (res, name) => {
    return (res?.publication_counts || []).map(
      (x) =>
        new SourceEntity({
          id: String(x.id),
          type: x.type,
          count: x.count || 0,
          symbol: x.symbol || "",
          name: calcEntityName(x, name),
        })
    );
  }
);

const getPublicationCountPerType = createSelector(
  getPublicationCountEntities,
  (res) =>
    res.reduce((a, i) => {
      a[i.type] = (a[i.type] || 0) + i.count;
      return a;
    }, {})
);

const getSourceName = createSelector(
  getVariantResult,
  getQueryType,
  getEntityName,
  (res, type, name) => {
    return type === GENE ? res?.symbol : name;
  }
);

const getSearchEntity = createSelector(
  getVariantResult,
  getQueryType,
  getSelectedGeneIndexClinical,
  getSelectedUserViewClinical,
  getSourceName,
  (res, queryType, geneInd, userView, name) => {
    const isClinical = isPortal();
    const isGene = queryType === GENE || userView === GENE;
    const isRegion = queryType === REGION;
    const id = isGene ? res?.gene_id : res?.variant_id;
    const type = isGene ? GENE : VARIANT;

    if (isRegion) {
      return new SourceEntity({ type: REGION, id, name: id });
    }

    if (isClinical && isGene) {
      const geneId = res?.genes?.[geneInd]?.gene_id;
      const symbol = res?.genes?.[geneInd]?.symbol;
      return new SourceEntity({ type: GENE, id: geneId, name: symbol, symbol });
    }

    return new SourceEntity({ type, id, name, symbol: res?.symbol });
  }
);

const getSearchEntry = createSelector(getSearchEntity, (entity) => {
  if (entity?.type !== VARIANT) return new PubmedEntry();
  const variants = entity?.id ? [{ variant_id: entity.id }] : [];
  return new PubmedEntry({ json_data: { variants } });
});

const getCommentsCount = (state) => {
  const threadMap = getThreadMap(state);
  const searchEntity = getSearchEntity(state);
  if (!searchEntity) return 0;
  const slug = searchEntity.getSlugForComments();
  return (threadMap || new Map()).get(slug)?.comments?.length || 0;
};

const getIsThrottling = createSelector(getPublications, (publications) =>
  Boolean(publications?.throttlingInfo)
);

export {
  getPublicationCountPerType,
  getCommentsCount,
  getPublications,
  getGenesItems,
  getVariantResultSelector,
  getSearchEntity,
  getPublicationCountEntities,
  getSearchEntry,
  getIsThrottling,
};
